import { useCallback, useEffect, useRef, useState } from "react";
import { listQaAutoRuns, type QaAutoRun } from "../api/qaAuto";
import { absoluteTime } from "../lib/time";

type Props = { project: string; isAdmin: boolean };

const PAGE_SIZE = 20;

// Admin-only run-history table for a pack's Automatic-QA loop. Pages through the
// daemon's /qa/auto/runs (newest first), PAGE_SIZE per page. The newest page
// (offset 0) auto-refreshes every 10s; older pages are a stable snapshot so
// paging back does not shift under you. Each run links to its qa-agent report
// page (/qa/<jobId>) from both the timestamp and the job number. Renders nothing
// for non-admins or when there is no history yet. Soft-degrades: a fetch failure
// keeps the last-known rows.
export default function QaAutoRuns({ project, isAdmin }: Props) {
  const [runs, setRuns] = useState<QaAutoRun[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    try {
      const page = await listQaAutoRuns(project, { limit: PAGE_SIZE, offset });
      setRuns(page.runs);
      setTotal(page.total);
    } catch { /* keep last-known */ }
  }, [project, offset]);

  // Reset to the newest page whenever the pack changes.
  useEffect(() => { setOffset(0); }, [project]);

  useEffect(() => {
    if (!isAdmin) return;
    void refresh();
    // Only the newest page polls; older pages are a fixed snapshot.
    if (offset === 0) {
      timer.current = setInterval(() => void refresh(), 10000);
      return () => { if (timer.current) clearInterval(timer.current); };
    }
    return undefined;
  }, [isAdmin, refresh, offset]);

  if (!isAdmin) return null;
  if (total === 0 && runs.length === 0) return null;

  const first = total === 0 ? 0 : offset + 1;
  const last = offset + runs.length;
  const hasPrev = offset > 0;
  const hasNext = offset + PAGE_SIZE < total;

  return (
    <div className="qa-auto-runs">
      <h2 className="qa-auto-runs-title">Automatic runs</h2>
      <table>
        <thead>
          <tr><th>When</th><th>Job</th><th>Branch</th><th>Status</th><th>New</th><th>Total</th><th>Shots</th></tr>
        </thead>
        <tbody>
          {runs.map((r) => (
            <tr key={r.id} className={`qa-auto-run qa-auto-run-${r.status}`}>
              <td>
                {r.report_url
                  ? <a href={r.report_url} target="_blank" rel="noreferrer">{absoluteTime(r.started_at)}</a>
                  : absoluteTime(r.started_at)}
              </td>
              <td>
                {r.job_id != null
                  ? (r.report_url
                      ? <a href={r.report_url} target="_blank" rel="noreferrer">#{r.job_id}</a>
                      : <>#{r.job_id}</>)
                  : "—"}
              </td>
              <td>{r.branch ?? "—"}</td>
              <td>{r.verdict ?? r.status}</td>
              <td>{r.findings_new}</td>
              <td>{r.findings_total}</td>
              <td>{r.screenshot_url ? <a href={r.screenshot_url} target="_blank" rel="noreferrer">view</a> : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {total > PAGE_SIZE && (
        <div className="qa-auto-runs-pager">
          <button
            type="button"
            className="qa-auto-runs-prev"
            disabled={!hasPrev}
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
          >Newer</button>
          <span className="qa-auto-runs-range">{first}–{last} of {total}</span>
          <button
            type="button"
            className="qa-auto-runs-next"
            disabled={!hasNext}
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
          >Older</button>
        </div>
      )}
    </div>
  );
}
